import { Alert, Button } from '@mui/material';
import useLocalization from './hooks/useLocalization';
import FullScreenLoader from './components/atoms/FullScreenLoader';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { bnBD } from '@mui/x-date-pickers/locales';
import { AuthProvider } from '@neotech-solutions-org/neofusion-fe-shared';
import { SnackbarProvider } from 'notistack';
import { Suspense, useEffect, useMemo } from 'react';
import StyledSnackbar from './components/atoms/StyledSnackbar';
import { getAuthConfig } from './constants/keycloak';
import { SkinProvider } from './contexts/SkinContext';
import { LanguageInstanceProvider } from './localization/LanguageInstanceProvider';
import GrowthBookProviderWrapper from './providers/GrowthBookProviderWrapper';
import { useMarketplaceConfig } from './queries/marketplace';
import LayoutRouter from './components/layouts/LayoutRouter';
import EmailVerificationGuard from './providers/EmailVerificationGuard';
import KycVerificationGuard from './providers/KycVerificationGuard';
import useBreakpoints from './hooks/useBreakpoints';
import useHandleComplyCubeReturn from './hooks/useHandleComplyCubeReturn';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { useFirstDepositTracking } from './hooks/useFirstDepositTracking';
import { usePrefetchManager } from './queries/prefetch/usePrefetchManager';
import OpenReplayProvider from './providers/OpenReplayProvider';
import { initializeGtm } from './utils/gtm';

const AuthenticatedAppContent = ({ isMobile }: { isMobile: boolean }) => {
  useFirstDepositTracking();
  const { i18n } = useLocalization();

  useEffect(() => {
    initializeGtm();
  }, []);

  return (
    <>
      <OpenReplayProvider />
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={i18n.language}
        localeText={
          i18n.language === 'bn' ? bnBD.components.MuiLocalizationProvider.defaultProps.localeText : undefined
        }
      >
        <SnackbarProvider
          Components={{
            success: StyledSnackbar,
            info: StyledSnackbar,
            warning: StyledSnackbar,
            error: StyledSnackbar,
          }}
          classes={{ containerAnchorOriginTopCenter: 'snackbar-container' }}
          preventDuplicate
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          dense={isMobile}
          hideIconVariant
          maxSnack={5}
          autoHideDuration={3000}
          disableWindowBlurListener
        >
          <Suspense fallback={<FullScreenLoader />}>
            <EmailVerificationGuard>
              <KycVerificationGuard>
                <LayoutRouter />
              </KycVerificationGuard>
            </EmailVerificationGuard>
          </Suspense>
        </SnackbarProvider>
      </LocalizationProvider>
    </>
  );
};

const MainLayout: React.FC = () => {
  const { data: marketplaceConfig, isLoading: isLoadingMarketplaceConfig, isError, refetch } = useMarketplaceConfig();
  const { t } = useLocalization();
  const { isMobile } = useBreakpoints();
  useGoogleAnalytics();
  useHandleComplyCubeReturn();

  const { prefetchInitialData } = usePrefetchManager();

  useEffect(() => {
    if (!isLoadingMarketplaceConfig && marketplaceConfig) {
      prefetchInitialData();
    }
  }, [isLoadingMarketplaceConfig, marketplaceConfig, prefetchInitialData]);

  const authConfig = useMemo(() => {
    return getAuthConfig(marketplaceConfig?.marketplaceConfig.model);
  }, [marketplaceConfig]);

  if (!marketplaceConfig) {
    return (
      <>
        <div data-testid='app-shell-ready' hidden />
        {isError ? (
          <Alert severity='error' action={<Button onClick={() => refetch()}>{t('retry')}</Button>}>
            {t('somethingWentWrong')}
          </Alert>
        ) : (
          <FullScreenLoader />
        )}
      </>
    );
  }

  return (
    <>
      <div data-testid='app-shell-ready' hidden />
      <AuthProvider config={authConfig}>
        <GrowthBookProviderWrapper>
          <SkinProvider>
            <LanguageInstanceProvider>
              <AuthenticatedAppContent isMobile={isMobile} />
            </LanguageInstanceProvider>
          </SkinProvider>
        </GrowthBookProviderWrapper>
      </AuthProvider>
    </>
  );
};

export default MainLayout;
